/**
 * CrosshairInfoBox (R115 Stage 2, restructured in R131) — single always-on
 * tooltip for the hovered bar:
 *
 *   1. OHLC + Change% — ALWAYS from the RAW (Normal) candle, regardless of
 *      the display candle type. Close + Change% render larger for quick
 *      reading.
 *   2. Trend — OverView TF / HTF scores (when available).
 *   3. Strategy — the applied chart strategy's running position, and on a
 *      signal candle a bold accent row naming the firing condition with a
 *      plain-English description (multi-condition strategies like Sniper
 *      show WHICH condition fired — B_C1…S_C3 — and what it means).
 *   4. VPA — when the VPA indicator is on and this bar carries VPA signals,
 *      each signal's firing-condition description (same text as the Chart
 *      Settings toggles).
 *
 * (BOLev_D / BOLev_W rows retired in R131 — the on-chart BOLev overlay is
 * unaffected.)
 *
 * Pure presentation — all data is computed by TradingChart and passed in via
 * `ctx`. The box smart-flips edges so it stays inside the chart container.
 */
import React from 'react';
import { Box } from '@mui/material';
import { OVERVIEW_ZONE_COLORS, hexToRgba } from '../../theme/overviewColors';
import type { OverviewZone } from '../../theme/overviewColors';
import { describeStrategySignal } from '../../utils/strategies/signalLabels';
import type { StrategyNote } from '../../utils/strategies/types';
import type { FnoLine } from '../../utils/fnoInterpret';

export type StrategySig = {
  signal: 'BUY' | 'SHORT' | 'EXIT_LONG' | 'EXIT_SHORT';
  strength: 'HIGH' | 'MEDIUM' | 'LOW' | null;
  /** Condition tag for multi-condition strategies (e.g. Sniper B_C1). */
  reason?: string | null;
};

export interface VpaTooltipSignal {
  /** Marker text as shown on the chart (e.g. "UT1", "ST·SV"). */
  text: string;
  color: string;
  /** Resolved signal definitions (label + the Chart-Settings description). */
  defs: { code: string; label: string; tip: string }[];
}

export interface OvSnapshot {
  score: number;
  zone: OverviewZone;
}

export interface CrosshairCtx {
  /** Cursor X / Y inside the chart container (px). */
  x: number;
  y: number;
  /** Container size for edge-flip math. */
  containerW: number;
  containerH: number;
  /** Theme. */
  isDark: boolean;
  /** Symbol (header). */
  symbol: string;
  /** Interval label (e.g. "90m"). */
  intervalLabel: string;
  /** OHLC of the hovered bar — RAW (Normal) candle values. Always present. */
  ohlc: { time: number; open: number; high: number; low: number; close: number };
  /** % change vs the previous bar's RAW close. */
  pctChange: number | null;
  /** Decimal precision for price formatting. */
  decimalPrecision: number;
  /** OverView trend scores at the hovered bar (when available). */
  tfScore?: OvSnapshot;
  htfScore?: OvSnapshot;
  /** OverView axis labels (e.g. "1H", "Daily") for context. */
  tfLabel?: string;
  htfLabel?: string;
  /** Applied chart strategy id ('rockband' | 'sniper' | 'dailyblast' | …). */
  strategyId?: string;
  /** Running strategy position at the hovered bar (when the strategy emits it). */
  strategyPos?: 'LONG' | 'SHORT' | 'FLAT';
  /** Fresh strategy signal at the hovered bar (when one fired). */
  strategySignal?: StrategySig | null;
  /** VPA signals on this bar (only when the VPA indicator is on). */
  vpaSignals?: VpaTooltipSignal[];
  /** Informational, non-trade marks on this bar — OM_Simple's W / C. Several
   *  may share a bar, and one may share it with a BUY/SHORT, which is why this
   *  is an array and not another key on the one-per-bar signal map. */
  strategyNotes?: StrategyNote[];
  /** FNO interpretation at the hovered PRICE level (only when the FNO indicator is
   *  on for an option symbol). Each line = one actionable read + a learner note. */
  fno?: FnoLine[];
  /** Hovered price (for the FNO section header). */
  fnoPrice?: number;
  /** Composite Bull/Bear rank at the hovered candle close. */
  fnoScore?: { score: number; zone: OverviewZone; label: string };
}

const ZONE_TEXT: Record<OverviewZone, string> = {
  strongBuy: 'Strong Buy',
  probableBuy: 'Prob Buy',
  neutral: 'Neutral',
  probableShort: 'Prob Short',
  strongShort: 'Strong Short',
};

/** Display names for the strategy section title. */
const STRATEGY_NAME: Record<string, string> = {
  rockband: 'RockBand',
  sniper: 'Sniper',
  dailyblast: 'DailyBlast',
  shaved_doji: 'Shaved Doji',
  thetaminer: 'ThetaMiner',
  bhairav: 'Bhairav',
  om: 'Om',
  om_simple: 'OM_Simple',
};

export const CrosshairInfoBox: React.FC<{ ctx: CrosshairCtx | null }> = ({ ctx }) => {
  if (!ctx) return null;
  const { isDark, ohlc, decimalPrecision, pctChange } = ctx;
  const fmt = (v: number | null | undefined) => v == null || !Number.isFinite(v) ? '—' : v.toFixed(Math.max(0, Math.min(8, Math.floor(decimalPrecision))));
  const dtFmt = (sec: number) => {
    // sec is IST-shifted (epoch + 19800). Display as DD-MMM HH:mm in IST.
    const ms = (sec - 19800) * 1000;
    const d = new Date(ms);
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const hh = String(d.getUTCHours() + 5).padStart(2, '0');
    const minRaw = d.getUTCMinutes() + 30;
    const minOver = minRaw >= 60;
    const mm = String(minOver ? minRaw - 60 : minRaw).padStart(2, '0');
    const dd = d.getUTCDate() + (minOver && (d.getUTCHours() + 5) >= 24 ? 1 : 0);
    const mo = months[d.getUTCMonth()];
    return `${String(dd).padStart(2,'0')}-${mo} ${hh}:${mm}`;
  };

  const bullCol = '#26a69a';
  const bearCol = '#ef5350';
  const pctCol = pctChange == null ? 'text.secondary' : (pctChange >= 0 ? bullCol : bearCol);
  const upBody = ohlc.close >= ohlc.open;
  const bodyCol = upBody ? bullCol : bearCol;

  const strategyName = ctx.strategyId ? (STRATEGY_NAME[ctx.strategyId] || ctx.strategyId) : null;
  const condLabel = ctx.strategySignal ? describeStrategySignal(ctx.strategyId, ctx.strategySignal) : null;
  const condCol = condLabel?.tone === 'bull' ? bullCol : condLabel?.tone === 'bear' ? bearCol : '#ff9800';

  // ── Position (edge-flip) ──────────────────────────────────────────────
  const tipW = 320;
  // Estimate height from how many sections we render (kept conservative).
  let estH = 70;
  if (ctx.tfScore || ctx.htfScore) estH += 22;
  if (ctx.fno?.length) estH += 16 + (ctx.fnoScore ? 22 : 0) + ctx.fno.length * 30;
  if (ctx.strategyPos) estH += 20;
  if (condLabel) estH += 44;
  if (ctx.vpaSignals?.length) estH += 18 + ctx.vpaSignals.reduce((a, s) => a + s.defs.length * 38 + 8, 0);
  if (ctx.strategyNotes?.length) estH += 18 + ctx.strategyNotes.length * 38;
  let left = ctx.x + 16;
  let top = ctx.y + 16;
  if (left + tipW > ctx.containerW - 6) left = Math.max(6, ctx.x - tipW - 16);
  if (top + estH > ctx.containerH - 30) top = Math.max(6, ctx.y - estH - 12);

  const sectionTitle = (t: string) => (
    <Box sx={{ fontSize: '0.54rem', fontWeight: 800, letterSpacing: 0.4, color: 'text.secondary' }}>{t}</Box>
  );

  return (
    <Box sx={{
      position: 'absolute', left, top,
      bgcolor: isDark ? 'rgba(13,17,23,0.94)' : 'rgba(255,255,255,0.96)',
      border: `1px solid ${isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.12)'}`,
      borderRadius: 1.2, px: 1.1, py: 0.6,
      color: 'text.primary', pointerEvents: 'none', zIndex: 8,
      boxShadow: isDark ? '0 4px 14px rgba(0,0,0,0.55)' : '0 4px 14px rgba(0,0,0,0.18)',
      width: tipW, maxWidth: tipW,
      display: 'flex', flexDirection: 'column', gap: 0.32,
    }}>
      {/* ── Header: symbol + interval + date/time ────────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ fontWeight: 800, fontSize: '0.72rem' }}>{ctx.symbol}</Box>
        <Box sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'text.secondary' }}>{ctx.intervalLabel}</Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'text.secondary' }}>{dtFmt(ohlc.time)}</Box>
      </Box>

      {/* ── OHLC row — raw (Normal) candle; Close + Change% read LARGE ── */}
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.6, flexWrap: 'wrap', fontFamily: 'monospace', fontSize: '0.62rem' }}>
        <Box><Box component="span" sx={{ color: 'text.secondary' }}>O</Box> <b>{fmt(ohlc.open)}</b></Box>
        <Box><Box component="span" sx={{ color: 'text.secondary' }}>H</Box> <b>{fmt(ohlc.high)}</b></Box>
        <Box><Box component="span" sx={{ color: 'text.secondary' }}>L</Box> <b>{fmt(ohlc.low)}</b></Box>
        <Box sx={{ flex: 1 }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, fontFamily: 'monospace' }}>
        <Box sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>C</Box>
        <Box sx={{ fontSize: '0.86rem', fontWeight: 800, color: bodyCol }}>{fmt(ohlc.close)}</Box>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ fontSize: '0.82rem', fontWeight: 800, color: pctCol }}>
          {pctChange == null ? '' : (pctChange >= 0 ? '+' : '') + pctChange.toFixed(2) + '%'}
        </Box>
      </Box>

      {/* ── OverView scores (TF / HTF) ───────────────────────────────── */}
      {(ctx.tfScore || ctx.htfScore) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap', fontSize: '0.6rem' }}>
          {sectionTitle('Trend')}
          {ctx.tfScore && (() => {
            const c = OVERVIEW_ZONE_COLORS[ctx.tfScore.zone];
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Box sx={{ fontSize: '0.54rem', color: 'text.secondary' }}>{`TF${ctx.tfLabel ? ' ' + ctx.tfLabel : ''}`}</Box>
                <Box sx={{ fontFamily: 'monospace', fontWeight: 800, color: c }}>{ctx.tfScore.score > 0 ? '+' : ''}{ctx.tfScore.score}</Box>
                <Box sx={{ fontSize: '0.54rem', fontWeight: 700, color: c }}>{ZONE_TEXT[ctx.tfScore.zone]}</Box>
              </Box>
            );
          })()}
          {ctx.htfScore && (() => {
            const c = OVERVIEW_ZONE_COLORS[ctx.htfScore.zone];
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                <Box sx={{ fontSize: '0.54rem', color: 'text.secondary' }}>{`HTF${ctx.htfLabel ? ' ' + ctx.htfLabel : ''}`}</Box>
                <Box sx={{ fontFamily: 'monospace', fontWeight: 800, color: c }}>{ctx.htfScore.score > 0 ? '+' : ''}{ctx.htfScore.score}</Box>
                <Box sx={{ fontSize: '0.54rem', fontWeight: 700, color: c }}>{ZONE_TEXT[ctx.htfScore.zone]}</Box>
              </Box>
            );
          })()}
        </Box>
      )}

      {/* ── FNO interpretation at the hovered price (option symbols only) ─ */}
      {!!ctx.fno?.length && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.22, mt: 0.2 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            {sectionTitle('FNO @ close')}
            {ctx.fnoPrice != null && <Box sx={{ fontFamily: 'monospace', fontSize: '0.54rem', color: 'text.disabled' }}>{fmt(ctx.fnoPrice)}</Box>}
          </Box>
          {ctx.fnoScore && (() => {
            const c = OVERVIEW_ZONE_COLORS[ctx.fnoScore.zone];
            const s = ctx.fnoScore.score;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.5, py: 0.3, borderRadius: 0.6, bgcolor: hexToRgba(c, 0.16) }}>
                <Box sx={{ fontSize: '0.56rem', fontWeight: 700, color: 'text.secondary' }}>Bull/Bear rank</Box>
                <Box sx={{ fontFamily: 'monospace', fontSize: '0.74rem', fontWeight: 800, color: c }}>{s > 0 ? '+' : ''}{s}</Box>
                <Box sx={{ fontSize: '0.58rem', fontWeight: 800, color: c }}>{ctx.fnoScore.label}</Box>
              </Box>
            );
          })()}
          {ctx.fno.map((l, i) => {
            const col = l.tone === 'good' ? bullCol : l.tone === 'bad' ? bearCol : '#90a4ae';
            return (
              <Box key={`${l.dim}-${i}`} sx={{
                px: 0.5, py: 0.25, borderRadius: 0.6,
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
                borderLeft: `3px solid ${col}`,
              }}>
                <Box sx={{ fontWeight: 800, fontSize: '0.58rem', color: col, lineHeight: 1.25 }}>
                  <Box component="span" sx={{ color: 'text.disabled', fontWeight: 700 }}>{l.dim}: </Box>{l.outcome}
                </Box>
                <Box sx={{ fontSize: '0.53rem', color: 'text.secondary', lineHeight: 1.2 }}>{l.explain}</Box>
              </Box>
            );
          })}
        </Box>
      )}

      {/* ── Strategy position (whichever strategy is applied) ────────── */}
      {strategyName && ctx.strategyPos && (() => {
        const pos = ctx.strategyPos!;
        const col = pos === 'LONG' ? bullCol : pos === 'SHORT' ? bearCol : '#9e9e9e';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.6rem' }}>
            {sectionTitle(strategyName)}
            <Box sx={{ fontWeight: 800, color: col, fontSize: '0.62rem' }}>{pos}</Box>
          </Box>
        );
      })()}

      {/* ── Strategy signal-candle row (condition + plain English) ───── */}
      {condLabel && (
        <Box sx={{
          mt: 0.2, px: 0.5, py: 0.35, borderRadius: 0.6,
          bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
          borderLeft: `3px solid ${condCol}`,
          display: 'flex', flexDirection: 'column', gap: 0.1,
        }}>
          <Box sx={{ fontWeight: 800, color: condCol, fontSize: '0.66rem' }}>
            {condLabel.tone === 'bull' ? '▲ ' : condLabel.tone === 'bear' ? '▼ ' : '◆ '}
            {strategyName ? `${strategyName} ` : ''}{condLabel.heading}
          </Box>
          <Box sx={{ fontSize: '0.56rem', color: 'text.secondary', lineHeight: 1.25 }}>{condLabel.detail}</Box>
        </Box>
      )}

      {/* ── VPA signals on this bar (only when the VPA indicator is on) ─ */}
      {!!ctx.strategyNotes?.length && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.2 }}>
          {sectionTitle(`${strategyName || 'Strategy'} marks`)}
          {ctx.strategyNotes.map((n, i) => (
            <Box key={`${n.code}-${i}`} sx={{
              px: 0.5, py: 0.3, borderRadius: 0.6,
              bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
              borderLeft: `3px solid ${n.color}`,
              display: 'flex', flexDirection: 'column', gap: 0.05,
            }}>
              <Box sx={{ fontWeight: 800, fontSize: '0.6rem', color: n.color }}>
                {n.code} · {n.label}{n.score != null ? ` · ${n.score}/100` : ''}
              </Box>
              <Box sx={{ fontSize: '0.54rem', color: 'text.secondary', lineHeight: 1.25 }}>{n.tip}</Box>
            </Box>
          ))}
        </Box>
      )}

      {!!ctx.vpaSignals?.length && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3, mt: 0.2 }}>
          {sectionTitle('VPA')}
          {ctx.vpaSignals.map((s, i) => (
            <Box key={`${s.text}-${i}`} sx={{
              px: 0.5, py: 0.3, borderRadius: 0.6,
              bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.035)',
              borderLeft: `3px solid ${s.color}`,
              display: 'flex', flexDirection: 'column', gap: 0.15,
            }}>
              {s.defs.length ? s.defs.map(d => (
                <Box key={d.code} sx={{ display: 'flex', flexDirection: 'column', gap: 0.05 }}>
                  <Box sx={{ fontWeight: 800, fontSize: '0.6rem', color: s.color }}>{s.text} · {d.label}</Box>
                  <Box sx={{ fontSize: '0.54rem', color: 'text.secondary', lineHeight: 1.25 }}>{d.tip}</Box>
                </Box>
              )) : (
                <Box sx={{ fontWeight: 800, fontSize: '0.6rem', color: s.color }}>{s.text}</Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};
